import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import './AdminFormStyles.css';

interface CAPIConfig {
  id?: number;
  pixel_id: string;
  access_token: string;
  test_event_code: string;
  name: string;
  is_active: boolean;
  send_purchase_events: boolean;
  send_add_to_cart_events: boolean;
  send_initiate_checkout_events: boolean;
  send_view_content_events: boolean;
  send_lead_events: boolean;
}

interface CAPIFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CAPIConfig) => Promise<void>;
  config?: CAPIConfig | null;
}

const CAPIFormModal: React.FC<CAPIFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  config
}) => {
  const [formData, setFormData] = useState<CAPIConfig>({
    pixel_id: '',
    access_token: '',
    test_event_code: '',
    name: '',
    is_active: true,
    send_purchase_events: true,
    send_add_to_cart_events: true,
    send_initiate_checkout_events: true,
    send_view_content_events: false,
    send_lead_events: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (config) {
      setFormData({
        id: config.id,
        pixel_id: config.pixel_id || '',
        access_token: '', // Don't pre-fill access token for security
        test_event_code: config.test_event_code || '',
        name: config.name || '',
        is_active: config.is_active ?? true,
        send_purchase_events: config.send_purchase_events ?? true,
        send_add_to_cart_events: config.send_add_to_cart_events ?? true,
        send_initiate_checkout_events: config.send_initiate_checkout_events ?? true,
        send_view_content_events: config.send_view_content_events ?? false,
        send_lead_events: config.send_lead_events ?? true
      });
    } else {
      setFormData({
        pixel_id: '',
        access_token: '',
        test_event_code: '',
        name: '',
        is_active: true,
        send_purchase_events: true,
        send_add_to_cart_events: true,
        send_initiate_checkout_events: true,
        send_view_content_events: false,
        send_lead_events: true
      });
    }
    setErrors({});
  }, [config, isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors((prev: any) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors: any = {};

    if (!formData.pixel_id.trim()) {
      newErrors.pixel_id = 'Pixel ID is required';
    } else if (!formData.pixel_id.match(/^\d{15,16}$/)) {
      newErrors.pixel_id = 'Pixel ID should be 15-16 digits';
    }

    if (!config && !formData.access_token.trim()) {
      newErrors.access_token = 'Access token is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    setLoading(true);
    try {
      // Only include access_token if it's provided (for updates, empty means keep existing)
      const submitData = { ...formData };
      if (config && !submitData.access_token) {
        delete (submitData as any).access_token;
      }
      await onSave(submitData);
      onClose();
    } catch (error: any) {
      if (error.response?.data) {
        setErrors(error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={config ? 'Edit CAPI Configuration' : 'Add CAPI Configuration'}
      size="large"
    >
      <form onSubmit={handleSubmit} className="admin-form-modal">
        <div className="admin-form-table">
          {/* Pixel ID */}
          <div className="admin-form-row">
            <div className="admin-form-label">Pixel ID *</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="pixel_id"
                value={formData.pixel_id}
                onChange={handleChange}
                placeholder="1234567890123456"
                className={errors.pixel_id ? 'has-error' : ''}
              />
              {errors.pixel_id && <span className="error-text">{errors.pixel_id}</span>}
            </div>
          </div>

          {/* Configuration Name */}
          <div className="admin-form-row">
            <div className="admin-form-label">Configuration Name</div>
            <div className="admin-form-input">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="My Store CAPI"
              />
            </div>
          </div>

          {/* Access Token */}
          <div className="admin-form-row">
            <div className="admin-form-label">Access Token {!config && '*'}</div>
            <div className="admin-form-input">
              <textarea
                name="access_token"
                value={formData.access_token}
                onChange={handleChange}
                placeholder={config ? 'Leave empty to keep existing token' : 'EAAxxxxxxxx...'}
                className={errors.access_token ? 'has-error' : ''}
                rows={3}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
              {errors.access_token && <span className="error-text">{errors.access_token}</span>}
            </div>
          </div>

          {/* Mode Selection - Test vs Production */}
          <div className="admin-form-row" style={{ backgroundColor: formData.test_event_code ? '#fff7ed' : '#f0fdf4' }}>
            <div className="admin-form-label">
              <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '8px'
              }}>
                Mode
                <span style={{
                  background: formData.test_event_code ? '#fef3c7' : '#d1fae5',
                  color: formData.test_event_code ? '#d97706' : '#059669',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontWeight: '600',
                  fontSize: '11px'
                }}>
                  {formData.test_event_code ? '🧪 TESTING' : '🚀 PRODUCTION'}
                </span>
              </span>
            </div>
            <div className="admin-form-input">
              <input
                type="text"
                name="test_event_code"
                value={formData.test_event_code}
                onChange={handleChange}
                placeholder="Leave empty for production"
                style={{ 
                  borderColor: formData.test_event_code ? '#f59e0b' : '#22c55e',
                  backgroundColor: formData.test_event_code ? '#fffbeb' : '#f0fdf4'
                }}
              />
              <div style={{ 
                marginTop: '6px', 
                fontSize: '12px', 
                color: '#6b7280',
                lineHeight: '1.4'
              }}>
                {formData.test_event_code ? (
                  <span style={{ color: '#d97706' }}>
                    ⚠️ <strong>Testing Mode:</strong> Events will appear in Events Manager → Test Events only. 
                    <strong> Clear this field for production.</strong>
                  </span>
                ) : (
                  <span style={{ color: '#059669' }}>
                    ✅ <strong>Production Mode:</strong> Events will be sent to your real Pixel data. 
                    To test first, enter a Test Event Code from Events Manager → Test Events.
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Event Settings Section Header */}
          <div className="admin-form-row" style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #e5e7eb' }}>
            <div className="admin-form-label" style={{ fontWeight: '700', color: '#374151' }}>Event Settings</div>
            <div className="admin-form-input" style={{ fontSize: '13px', color: '#6b7280' }}>
              Select which events to send via CAPI
            </div>
          </div>

          {/* Purchase Events */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Purchase Events</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="send_purchase_events"
                  checked={formData.send_purchase_events}
                  onChange={handleChange}
                />
                <span>Send Purchase events (Recommended)</span>
              </label>
            </div>
          </div>

          {/* AddToCart Events */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">AddToCart Events</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="send_add_to_cart_events"
                  checked={formData.send_add_to_cart_events}
                  onChange={handleChange}
                />
                <span>Send AddToCart events</span>
              </label>
            </div>
          </div>

          {/* InitiateCheckout Events */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Checkout Events</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="send_initiate_checkout_events"
                  checked={formData.send_initiate_checkout_events}
                  onChange={handleChange}
                />
                <span>Send InitiateCheckout events</span>
              </label>
            </div>
          </div>

          {/* Lead Events */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">Lead Events</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="send_lead_events"
                  checked={formData.send_lead_events}
                  onChange={handleChange}
                />
                <span>Send Lead events</span>
              </label>
            </div>
          </div>

          {/* ViewContent Events */}
          <div className="admin-form-row checkbox-row">
            <div className="admin-form-label">ViewContent Events</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="send_view_content_events"
                  checked={formData.send_view_content_events}
                  onChange={handleChange}
                />
                <span>Send ViewContent events (High volume)</span>
              </label>
            </div>
          </div>

          {/* Active Status */}
          <div className="admin-form-row checkbox-row" style={{ borderTop: '2px solid #e5e7eb' }}>
            <div className="admin-form-label">Status</div>
            <div className="admin-form-input">
              <label className="admin-toggle-label">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <span>{formData.is_active ? 'Active - Server-side tracking enabled' : 'Inactive'}</span>
              </label>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="admin-form-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-save" disabled={loading}>
            {loading ? 'Saving...' : (config ? 'Update' : 'Create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CAPIFormModal;
